import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

// CSRF Protection - Automatic token handling

// Function to get CSRF token from cookie
const getCsrfTokenFromCookie = () => {
  const name = 'csrftoken';
  const value = "; " + document.cookie;
  const parts = value.split("; " + name + "=");
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

// Function to fetch CSRF token from server if not in cookie
// Cache for CSRF token
let csrfTokenCache = null;
let csrfTokenExpiry = null;

const fetchCsrfToken = async () => {
  // Check if token is cached and still valid
  if (csrfTokenCache && csrfTokenExpiry && new Date() < csrfTokenExpiry) {
    return csrfTokenCache;
  }

  try {
    await axiosInstance.get('/security/csrf-token');
    const token = getCsrfTokenFromCookie();
    if (token) {
      csrfTokenCache = token;
      csrfTokenExpiry = new Date(new Date().getTime() + 10 * 60 * 1000); // Cache for 10 minutes
    }
    return token;
  } catch (error) {
    console.warn('Failed to fetch CSRF token:', error);
    return null;
  }
};

// track refresh state
let isRefreshing = false;
let failedQueue = [];
let hasRedirected = false;

const processQueue = (error, response) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else      prom.resolve(response);
  });
  failedQueue = [];
};

const redirectToHome = () => {
  if (!hasRedirected && window.location.pathname !== "/" && window.location.pathname !== "/login") {
    hasRedirected = true;
    localStorage.clear();
    sessionStorage.clear();
    // Clear auth state and redirect to home
    console.log('🏠 Redirecting to home page due to auth failure');
    window.location.replace("/");
  }
};

// Reset redirect flag when we're on login or home page
if (window.location.pathname === "/login" || window.location.pathname === "/") {
  hasRedirected = false;
}

// Request interceptor with automatic CSRF token handling
axiosInstance.interceptors.request.use(
  async (config) => {
    console.debug('Request:', config.method?.toUpperCase(), config.url);
    
    // Add CSRF token for non-safe methods (POST, PUT, DELETE, PATCH)
    const unsafeMethods = ['post', 'put', 'delete', 'patch'];
    if (unsafeMethods.includes(config.method?.toLowerCase())) {
      // Skip CSRF for auth and security endpoints
      const isAuthEndpoint = config.url?.includes('/auth/') || 
                             config.url?.includes('/security/') ||
                             config.url?.includes('/users/create');
      
      if (!isAuthEndpoint) {
        // Get CSRF token from cookie first
        let token = getCsrfTokenFromCookie();
        
        // If no token in cookie, fetch from server
        if (!token) {
          token = await fetchCsrfToken();
        }
        
        // Add token to request header
        if (token) {
          config.headers['X-CSRFToken'] = token;
          console.debug('Added CSRF token to request:', config.url, token.substring(0, 20) + '...');
        } else {
          console.warn('No CSRF token found for request:', config.url);
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (resp) => {
    // Success response - log for debugging
    console.debug('Response:', resp.status, resp.config.url);
    return resp;
  },
  async (err) => {
    const { response, config } = err;
    const originalRequest = config;

    // Handle CSRF errors (403) by refetching token
    if (response?.status === 403 && 
        response?.data?.detail?.includes('CSRF') && 
        !originalRequest._csrfRetry) {
      console.log('🛡️ CSRF token expired, refetching...', {
        url: originalRequest.url,
        method: originalRequest.method,
        error: response.data
      });
      originalRequest._csrfRetry = true;
      
      // Fetch new CSRF token
      const newToken = await fetchCsrfToken();
      
      if (newToken) {
        // Retry original request with new token
        originalRequest.headers['X-CSRFToken'] = newToken;
        return axiosInstance(originalRequest);
      }
    }

    // Check for explicit logout header on any 401 response
    if (response?.status === 401 && response?.headers['x-logout-required'] === 'true') {
      console.log('🚪 Backend requested immediate logout due to auth failure');
      localStorage.clear();
      sessionStorage.clear();
      redirectToHome();
      return Promise.reject(err);
    }

    // only handle 401 once per request
    if (response?.status === 401 && !originalRequest._retry) {
      console.log('🔒 Access token expired or invalid, attempting automatic refresh...');

      // Prevent infinite loops
      if (hasRedirected) {
        console.log('❌ Already redirected, rejecting request');
        return Promise.reject(err);
      }
      
      // don't loop if /refresh itself fails
      if (originalRequest.url.includes("/auth/refresh")) {
        console.log('❌ Token refresh endpoint failed, redirecting to home');
        localStorage.clear();
        sessionStorage.clear();
        redirectToHome();
        return Promise.reject(err);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        // queue up all 401-fails while we're already refreshing
        console.log('⏳ Token refresh in progress, queuing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axiosInstance(originalRequest));
      }

      isRefreshing = true;

      return new Promise((resolve, reject) => {
        console.log('🔄 Attempting to refresh expired access token...');
        
        axiosInstance
          .post("/auth/refresh")  // Use relative path since baseURL includes /api/v1
          .then(res => {
            console.log('✅ Token refresh successful');
            // server set new cookies; drain the queue
            processQueue(null, res);
            resolve(axiosInstance(originalRequest));
          })
          .catch(refreshErr => {
            console.log('❌ Token refresh failed:', refreshErr.response?.status, refreshErr.response?.data?.detail);

            // Check for explicit logout header from backend
            if (refreshErr.response?.headers['x-logout-required'] === 'true') {
              console.log('🚪 Backend requested explicit logout - clearing session');
            }

            processQueue(refreshErr, null);
            // Clear storage and redirect to home page
            localStorage.clear();
            sessionStorage.clear();
            redirectToHome();
            reject(refreshErr);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(err);
  }
);

// Initialize CSRF token on app start
const initializeCSRF = async () => {
  try {
    await fetchCsrfToken();
    console.log('✅ CSRF protection initialized');
  } catch (error) {
    console.warn('⚠️ CSRF initialization failed:', error);
  }
};

// Auto-initialize CSRF when axios is imported
initializeCSRF();

export default axiosInstance;
